import { useEffect, useState } from 'react';
import SectionHeading from '@/components/base/SectionHeading';
import Reveal from '@/components/base/Reveal';
import { operationOrderPool, settlementParties, operationMetrics } from '@/mocks/experience';

interface OrderItem {
  buyer: string;
  role: string;
  item: string;
  status: string;
  time: string;
}

const initialOrders: OrderItem[] = [
  { buyer: '陈女士', role: '消费者', item: '数字聚餐套餐 A', status: '待核销', time: '10:32:08' },
  { buyer: '王老板', role: '云店长', item: '农家腊肉 500g', status: '已确认', time: '10:28:41' },
  { buyer: '李姐', role: '云社员', item: '县域土鸡蛋 30 枚', status: '履约中', time: '10:21:15' },
];

const roleTone: Record<string, string> = {
  消费者: 'bg-primary-100 text-primary-800',
  云社员: 'bg-accent-100 text-accent-800',
  云店长: 'bg-secondary-100 text-secondary-800',
  供应商: 'bg-accent-100 text-accent-800',
};

const statusTone: Record<string, string> = {
  已支付: 'text-accent-600',
  履约中: 'text-secondary-600',
  已确认: 'text-primary-600',
  待核销: 'text-foreground-500',
};

export default function OperationPanel() {
  const [orders, setOrders] = useState<OrderItem[]>(initialOrders);
  const [orderIndex, setOrderIndex] = useState(0);
  const [mounted, setMounted] = useState(false);
  const [pulse, setPulse] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 120);
    return () => clearTimeout(t);
  }, []);

  const simulateOrder = () => {
    const source = operationOrderPool[orderIndex % operationOrderPool.length];
    const now = new Date();
    const time = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(
      now.getSeconds()
    ).padStart(2, '0')}`;
    const newOrder: OrderItem = {
      buyer: source.buyer,
      role: source.role,
      item: source.item,
      status: '已支付',
      time,
    };
    setOrders((prev) => [newOrder, ...prev].slice(0, 8));
    setOrderIndex((i) => i + 1);
    setPulse(true);
    setTimeout(() => setPulse(false), 600);
  };

  return (
    <section className="w-full bg-foreground-950 py-16 md:py-24">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <Reveal>
          <SectionHeading
            dark
            eyebrow="交互运营面板"
            title="下单与清分数据看板（模拟）"
            subtitle="亲手模拟一笔订单，观察订单如何进入清分链路、七方贡献如何分配。所有数据均为模拟演示，用于呈现运营逻辑。"
          />
        </Reveal>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* 左侧：实时订单流 */}
          <Reveal delay={80}>
            <div className="h-full rounded-lg border border-foreground-700 bg-foreground-900 p-6 flex flex-col">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-accent-400 animate-live-blink"></span>
                  <h3 className="font-heading font-bold text-background-50">实时订单流</h3>
                </div>
                <span className="text-xs text-foreground-400">LIVE · 模拟</span>
              </div>

              <button
                onClick={simulateOrder}
                className={`mt-5 inline-flex items-center justify-center gap-2 w-full px-5 py-3 rounded-md font-medium text-sm whitespace-nowrap cursor-pointer transition-all ${
                  pulse
                    ? 'bg-accent-500 text-foreground-950 scale-[1.02]'
                    : 'bg-primary-500 text-background-50 hover:bg-primary-600'
                }`}
              >
                <i className="ri-flashlight-line"></i>
                模拟一笔下单
              </button>

              <div className="mt-5 flex-1 space-y-3 max-h-[420px] overflow-y-auto pr-1">
                {orders.map((o, i) => (
                  <div
                    key={`${o.time}-${i}`}
                    className={`rounded-md border p-4 transition-colors ${
                      i === 0 ? 'border-primary-500/40 bg-primary-500/10' : 'border-foreground-700 bg-foreground-950/40'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <span
                          className={`px-2 py-0.5 rounded text-[11px] whitespace-nowrap ${
                            roleTone[o.role] || 'bg-background-50/10 text-background-100'
                          }`}
                        >
                          {o.role}
                        </span>
                        <span className="text-sm text-background-100 font-medium truncate">{o.buyer}</span>
                      </div>
                      <span className="text-xs text-foreground-400 whitespace-nowrap">{o.time}</span>
                    </div>
                    <div className="mt-2">
                      <span className="text-sm text-foreground-300 truncate">{o.item}</span>
                    </div>
                    <div className="mt-1.5 text-xs">
                      <span className={statusTone[o.status] || 'text-foreground-400'}>{o.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>

          {/* 右侧：清分数据看板 */}
          <Reveal delay={140}>
            <div className="h-full rounded-lg border border-foreground-700 bg-foreground-900 p-6">
              <div className="flex items-center justify-between">
                <h3 className="font-heading font-bold text-background-50">清分数据看板</h3>
                <span className="text-xs text-foreground-400">七方共利 · 模拟</span>
              </div>

              {/* 指标 */}
              <div className="mt-5 grid grid-cols-2 gap-3">
                {operationMetrics.map((m) => (
                  <div key={m.label} className="rounded-md border border-foreground-700 bg-foreground-950/40 p-4">
                    <div className="w-9 h-9 rounded-lg bg-accent-500/20 flex items-center justify-center">
                      <i className={`${m.icon} text-accent-300 text-lg`}></i>
                    </div>
                    <div className="mt-3 font-heading font-bold text-xl text-background-50">{m.value}</div>
                    <div className="mt-0.5 text-xs text-foreground-400">{m.label}</div>
                  </div>
                ))}
              </div>

              {/* 清分占比 */}
              <div className="mt-6">
                <div className="text-sm font-medium text-background-100 mb-3">七方贡献清分占比</div>
                <div className="space-y-3">
                  {settlementParties.map((p, i) => (
                    <div key={p.name}>
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-foreground-300">
                          {p.name}
                          <span className="text-foreground-500 ml-1">· {p.note}</span>
                        </span>
                        <span className="text-background-50 font-semibold">{p.pct}%</span>
                      </div>
                      <div className="h-2 rounded-full bg-foreground-950/60 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-primary-500 to-accent-500 transition-all duration-1000 ease-out"
                          style={{
                            width: mounted ? `${p.pct}%` : '0%',
                            transitionDelay: `${i * 90}ms`,
                          }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <p className="mt-6 text-xs text-foreground-400 leading-relaxed">
                注：以上占比为「七方共利」清分机制的示意比例，用于展示价值分配逻辑，不代表实际分账规则；真实清分以平台规则与合同为准。
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}